import type React from "react";
import { useEffect, useState } from "react";
import type {
  ImageFormat,
  ImageItem,
  ImageSize,
} from "../../../services/imageUtils";
import {
  getBestImageUrl,
  getImageDimensions,
} from "../../../services/imageUtils";

interface ResponsiveImageProps {
  variants?: ImageItem;
  images?: string[]; // Fallback to images array when variants don't exist
  alt: string;
  sizes?: string;
  className?: string;
  loading?: "lazy" | "eager";
  priority?: boolean; // For above-the-fold images
  size?: ImageSize; // Size preference (defaults to thumb)
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

interface PictureSourceConfig {
  type: string;
  srcSet: string;
  sizes: string;
}

/**
 * Modern responsive image component with automatic format selection
 * Supports AVIF, WebP, and JPEG with progressive enhancement
 * Defaults to thumbnail size to minimize bandwidth usage
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  variants,
  images,
  alt,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  className,
  loading = "lazy",
  priority = false,
  size = "small", // Default to small instead of thumb
  onClick,
  onKeyDown,
}) => {
  const [pictureConfig, setPictureConfig] = useState<{
    sources: PictureSourceConfig[];
    fallbackSrc: string;
    fallbackSrcSet?: string;
  } | null>(null);

  useEffect(() => {
    const generatePictureConfig = () => {
      // If no variants, use the images array as fallback
      if (!variants || !variants.original) {
        if (images && images.length > 0) {
          return {
            sources: [],
            fallbackSrc: images[0],
          };
        }
        return null;
      }

      const sources: PictureSourceConfig[] = [];
      const formats: Array<{ format: ImageFormat; type: string }> = [
        { format: "avif", type: "image/avif" },
        { format: "webp", type: "image/webp" },
        { format: "jpeg", type: "image/jpeg" },
      ];

      // Generate sources for each format - limit sizes based on requested size
      const sizeHierarchy: ImageSize[] = ["small", "medium", "large"];
      const requestedSizeIndex = sizeHierarchy.indexOf(size);

      // For thumb using small
      let sizesToCheck: ImageSize[];

      sizesToCheck =
        requestedSizeIndex >= 0
          ? sizeHierarchy.slice(requestedSizeIndex, requestedSizeIndex + 2) // Start from requested size, include next larger for high-DPI
          : sizeHierarchy;

      for (const { format, type } of formats) {
        const srcSetEntries: string[] = [];

        for (const sizeToCheck of sizesToCheck) {
          const url = getBestImageUrl(variants, sizeToCheck, [format]);
          const dimensions = getImageDimensions(variants, sizeToCheck);

          if (url && dimensions) {
            srcSetEntries.push(`${url} ${dimensions.width}w`);
          }
        }

        if (srcSetEntries.length > 0) {
          sources.push({
            type,
            srcSet: srcSetEntries.join(", "),
            sizes,
          });
        }
      }

      // Generate fallback src and srcset (prefer requested size, fallback to available sizes)
      const fallbackSrc =
        getBestImageUrl(variants, size, ["webp", "jpeg"]) ||
        variants.original.url;

      // Create srcset for fallback img element (JPEG only for compatibility)
      const jpegSrcSetEntries: string[] = [];

      for (const sizeToCheck of sizesToCheck) {
        const url = getBestImageUrl(variants, sizeToCheck, ["jpeg"]);
        const dimensions = getImageDimensions(variants, sizeToCheck);

        if (url && dimensions) {
          jpegSrcSetEntries.push(`${url} ${dimensions.width}w`);
        }
      }

      return {
        sources,
        fallbackSrc,
        fallbackSrcSet:
          jpegSrcSetEntries.length > 0
            ? jpegSrcSetEntries.join(", ")
            : undefined,
      };
    };

    const config = generatePictureConfig();
    setPictureConfig(config);
  }, [variants, images, size, sizes]);

  // Show loading placeholder while determining best format
  if (!pictureConfig) {
    return (
      <div
        className={`bg-gray-200 animate-pulse ${className || ""}`}
        style={{ aspectRatio: "16/9" }}
        role="img"
        aria-label="Loading image..."
      />
    );
  }

  // Handle click events with proper accessibility
  const handleClick = onClick || (() => {});
  const handleKeyDown =
    onKeyDown ||
    ((event: React.KeyboardEvent) => {
      if ((event.key === "Enter" || event.key === " ") && onClick) {
        event.preventDefault();
        onClick();
      }
    });

  const imageElement = (
    <img
      src={pictureConfig.fallbackSrc}
      srcSet={pictureConfig.fallbackSrcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      loading={priority ? "eager" : loading}
      decoding="async"
    />
  );

  // If we have sources (multiple formats), use picture element
  if (pictureConfig.sources.length > 0) {
    const pictureElement = (
      <picture>
        {pictureConfig.sources.map((source) => (
          <source
            key={source.type}
            type={source.type}
            srcSet={source.srcSet}
            sizes={source.sizes}
          />
        ))}
        {imageElement}
      </picture>
    );

    // If clickable, wrap in button or div with proper accessibility
    if (onClick) {
      return (
        <button
          type="button"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={`${className || ""} responsive-image-button`}
          style={{
            border: "none",
            background: "none",
            padding: "0",
            cursor: "pointer",
            display: "block",
            width: "100%",
          }}
          aria-label={alt}
        >
          {pictureElement}
        </button>
      );
    }

    return pictureElement;
  }

  // Fallback to simple img element
  if (onClick) {
    return (
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${className || ""} responsive-image-button`}
        style={{
          border: "none",
          background: "none",
          padding: "0",
          cursor: "pointer",
          display: "block",
          width: "100%",
        }}
        aria-label={alt}
      >
        {imageElement}
      </button>
    );
  }

  return imageElement;
};

/**
 * Hook to get the optimal image URL from variants
 */
export const useOptimalImage = (
  variants?: ImageItem,
  images?: string[],
  size: ImageSize = "thumb"
) => {
  const [optimalUrl, setOptimalUrl] = useState<string | null>(null);

  useEffect(() => {
    if (variants) {
      const url = getBestImageUrl(variants, size, ["webp", "avif", "jpeg"]);
      setOptimalUrl(url || variants.original.url);
    } else if (images && images.length > 0) {
      setOptimalUrl(images[0]);
    } else {
      setOptimalUrl(null);
    }
  }, [variants, images, size]);

  return optimalUrl;
};

/**
 * Component for responsive background images
 */
interface ResponsiveBackgroundProps {
  variants?: ImageItem;
  images?: string[];
  size?: ImageSize;
  children: React.ReactNode;
  className?: string;
}

export const ResponsiveBackground: React.FC<ResponsiveBackgroundProps> = ({
  variants,
  images,
  size = "medium", // Use medium for backgrounds as they're typically larger
  children,
  className,
}) => {
  const backgroundUrl = useOptimalImage(variants, images, size);

  return (
    <div
      className={className}
      style={{
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {children}
    </div>
  );
};

/**
 * Gallery component for multiple responsive images
 */
interface ImageGalleryProps {
  items: Array<{
    variants?: ImageItem;
    images?: string[];
    alt: string;
    caption?: string;
  }>;
  onImageClick?: (index: number) => void;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  items,
  onImageClick,
}) => {
  return (
    <div className="image-gallery">
      {items.map((item, index) => (
        <div
          key={`gallery-${item.alt}-${item.variants?.original?.url || item.images?.[0] || index}`}
          className="gallery-item"
        >
          <ResponsiveImage
            variants={item.variants}
            images={item.images}
            alt={item.alt}
            onClick={() => onImageClick?.(index)}
            size="medium"
            className="gallery-image"
          />
          {item.caption && (
            <div className="gallery-caption">{item.caption}</div>
          )}
        </div>
      ))}
    </div>
  );
};
